import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Text,
  VStack,
  Checkbox,
  CheckboxGroup,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import { cqAPI } from "../../lib/Auth";
import _ from "lodash";
import dialogManager from "../DialogManager/DialogManager";
import Swal from 'sweetalert2';

const NotificationSettings = () => {
  const [notificationSettings, setNotificationSettings] = useState({
    preferredChannelsByTopicsMap: {},
    topics: {},
    channels: [],
    business: { name: "" },
    user: {},
    active_plan: {
      features: {},
    },
  });
  const [originalSettings, setOriginalSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  // Numbered on the way OUT, and only the newest-issued read may paint. Every path here —
  // first load, post-save refresh — takes a number from the same counter, so a read that
  // was overtaken while it ran is dropped when it lands rather than installing a view of
  // the settings that something newer has already replaced.
  const readIssuedRef = useRef(0);
  // Bumped by every edit. A save carries the number it was started from, so its answer
  // can tell whether the switches on screen are still the ones it sent.
  const editedAtRef = useRef(0);
  
  async function checkRTPermission(){
    if (window.top.Realtime.isPermissionDeniedExplicitly()) {
      window.top.Realtime.showPermissionDeniedAlert(Swal);
      return false;
    } else if (!window.top.Realtime.hasPermission()) {
      const result = await window.top.Realtime.requestPermissionIfNeeded(Swal);
      if (result !== window.top.Realtime.Status.Granted) {
        window.top.Realtime.showPermissionDeniedAlert(Swal);
        return false;
      }
    }
    return true
  }
  
  // `editedAt`: the edit count a save was built from, passed by the refresh that save
  // triggered. What the server confirmed is still the truth about what is STORED — so it
  // becomes the baseline the Save button compares against — but any switch the user has
  // moved since stays where they put it, and Save stays lit so they can commit it.
  //
  // The comparison happens HERE, immediately before the write, not when the POST
  // answered. A save answers and then goes and re-reads, and a switch moved during that
  // second request is just as much a newer choice as one moved during the first; deciding
  // early made an answer that predates it erase it, in full view, with the toast saying
  // it had been saved.
  const fetchNotificationSettings = async ({ editedAt } = {}) => {
    const at = (readIssuedRef.current += 1);
    const data = await cqAPI.get("/settings/notifications").then((x) => x.data);
    if (at !== readIssuedRef.current) return;
    const keepEdits = editedAt !== undefined && editedAtRef.current !== editedAt;
    if (!keepEdits) setNotificationSettings(data);
    if (data.requestBrowserPermission){
      await checkRTPermission()
    }
    if (at !== readIssuedRef.current) return;
    setOriginalSettings(_.cloneDeep(data));
  };

  useEffect(() => {
    fetchNotificationSettings();
  }, []);

  const handleCheckboxChange = (topicName, newValues) => {
    editedAtRef.current += 1;
    // Update the preferredChannelsByTopicsMap with new values for the specific topic
    setNotificationSettings((prevSettings) => ({
      ...prevSettings,
      preferredChannelsByTopicsMap: {
        ...prevSettings.preferredChannelsByTopicsMap,
        [topicName]: newValues,
      },
    }));
  };
  
  
  const handleSoundChange = (v)=>{
    editedAtRef.current += 1;
    setNotificationSettings({
      ...notificationSettings,
      notificationSettings : {
        sound : v.target.checked
      }
    })
  }

  useEffect(() => {
    const changesDetected = !_.isEqual(notificationSettings, originalSettings);
    setHasChanges(changesDetected);
  }, [notificationSettings, originalSettings]);

  const { topics, channels, business, preferredChannelsByTopicsMap } = notificationSettings;
  const bgColor = useColorModeValue("gray.100", "gray.700");

  const saveChanges = async () => {
    // The state this save is made of. If the user moves a switch while it is in flight,
    // the answer describes settings that are no longer the ones on screen.
    const sentAt = editedAtRef.current;
    const changes = {};
    Object.keys(notificationSettings).forEach((key) => {
      if (!_.isEqual(notificationSettings[key], originalSettings[key])) {
        changes[key] = notificationSettings[key];
      }
    });
    const updated = {
      ...changes.preferredChannelsByTopicsMap? {channels : changes.preferredChannelsByTopicsMap} : {},
      ...changes.notificationSettings? {sound : changes.notificationSettings.sound} : {}
    }
    const shouldAskForDesktopNotificaitonPermission = !!Object.keys(updated?.channels || {}).find(key=>updated.channels[key].indexOf('desktop') != -1);
    
    if (shouldAskForDesktopNotificaitonPermission) {
      if (!await checkRTPermission()){
        return
      }
    }
    const resp = await cqAPI.post('/settings/notifications',updated)
    if (resp.msg) {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: resp.msg,
        header: "Error",
      });
    }
    if (resp.type == 'ok'){
      fetchNotificationSettings({ editedAt: sentAt })
    }
  };

  return (
    <Box p={4} overflowY="auto" maxH="100vh" w="100%" align="left">
      <Box textAlign="right" mt={4} paddingBottom="25px">
        <Button colorScheme="blue" isDisabled={!hasChanges} onClick={saveChanges}>
          Save
        </Button>
      </Box>
    {Object.entries(topics).map(([topicName, topic]) => {
      const isEnabled = topic.channels && topic.channels.length > 0;
      if (isEnabled) {
        return (
          <Box key={topicName} mb={4} p={4} bgColor={bgColor} borderRadius="md">
            <Text mb={4}>{topic.title}</Text>
            {/* Controlled, not defaultValue: an uncontrolled group keeps its own copy of
                what is ticked, so after a refresh replaced the state the boxes could go on
                showing channels the server had just told us were off — the page saying
                'notifications are on' over settings that are off. */}
            <CheckboxGroup
              value={preferredChannelsByTopicsMap[topicName] || []}
              onChange={(newValues) => handleCheckboxChange(topicName, newValues)}
            >
              <VStack align="start">
                {channels.map((channel) =>
                  topic.channels.includes(channel) ? (
                    <Checkbox key={channel} value={channel}>
                      {channel}
                    </Checkbox>
                  ) : null
                )}
              </VStack>
            </CheckboxGroup>
          </Box>
        );
      }
      return null;
    })}
      {Object.keys(topics).length > 0 && (<Box key="desktopNotification" mb={4} p={4} bgColor={bgColor} borderRadius="md">
        <Text mb={4}>Desktop notification</Text>
        <Checkbox isChecked={!!notificationSettings?.notificationSettings?.sound} onChange={(newValues) => handleSoundChange(newValues) }>
          Play a sound
        </Checkbox>
    </Box>)}
    </Box>
  );
};

export default NotificationSettings;
